import { useQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SectionError from 'Shared/SectionError';
import GenericExploreContent from '../../../Explore/GenericExploreContent/GenericExploreContent';
import HomeSectionSkeleton from '../components/HomeSectionSkeleton';
import { useAssetToken } from '../hooks/useAssetToken';
import { useSectionErrorLogging } from '../hooks/useSectionErrorLogging';
import { HOME_SECTION_QUERY_OPTIONS } from '../sectionQueryOptions';
import { getToolsForYourDay } from '../utils';
import {
  HomeToolsForYourDay,
  HomeToolsForYourDayVariables,
} from './__generated__/HomeToolsForYourDay';
import { HOME_TOOLS_FOR_YOUR_DAY_QUERY } from './toolsForYourDayQueries';

/** One recommended tool for each part of the user's day. */
const ToolsForYourDay = () => {
  const { t: translate } = useTranslation('homepage');
  const userDateTimeISOFormat = useMemo(() => new Date().toISOString(), []);
  const { assetToken } = useAssetToken();

  const { data, loading, error, refetch } = useQuery<
    HomeToolsForYourDay,
    HomeToolsForYourDayVariables
  >(HOME_TOOLS_FOR_YOUR_DAY_QUERY, {
    ...HOME_SECTION_QUERY_OPTIONS,
    variables: { userLocalDateTime: userDateTimeISOFormat },
  });

  useSectionErrorLogging(error, data, 'tools-for-your-day');

  if (loading && !data) {
    return (
      <HomeSectionSkeleton testId="tools-for-your-day-skeleton" tileCount={3} />
    );
  }

  if (error && !data) {
    // `errorPolicy: 'none'` makes refetch() reject on failure; the hook's own
    // `data` and `error` still drive what renders.
    return (
      <SectionError
        onRetry={async () =>
          refetch({ userLocalDateTime: userDateTimeISOFormat }).catch(
            () => undefined,
          )
        }
      />
    );
  }

  const tools = getToolsForYourDay(
    data?.user?.dailySelection?.beforeWork ?? [],
    data?.user?.dailySelection?.duringWork ?? [],
    data?.user?.dailySelection?.afterWork ?? [],
  );

  if (!tools.length) {
    return null;
  }

  return (
    <section>
      <h2 className="title-s section-title">
        {translate('tools_for_your_day_section.title')}
      </h2>
      <GenericExploreContent
        content={tools}
        assetToken={assetToken}
        source="explore-tools-for-your-day"
      />
    </section>
  );
};

export default ToolsForYourDay;
